import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, saveSession, sizeLabel, usd, type Session } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';
import BarcodeScanner, { cameraScanSupported } from '../components/BarcodeScanner';
import MasterSearch from '../components/MasterSearch';
import { CustomerPicker } from '../components/RegisterTools';
import LotteryAudit from '../components/LotteryAudit';
import { ShaileshChat } from '../components/Shailesh';
import { LazyAI } from '../components/Lazy';

type Tab = 'ask' | 'lazy' | 'check' | 'receive' | 'presell' | 'lottery';

// Phone-sized tools for walking the floor: ask Shailesh AI, check a price, receive a delivery, pre-sell customers in line.
export default function Mobile({ session }: { session: Session }) {
  const [tab, setTab] = useState<Tab>('ask');
  const isManager = session.user.role !== 'cashier';
  const store = session.stores.find((s) => s.id === session.storeId);
  return (
    <div className="mobile">
      <header className="m-top">
        <div className="grow"><b>{store?.name ?? 'Store'}</b><div className="faint" style={{ color: '#9fb4a7' }}>{session.user.name}</div></div>
        <Link className="btn" to="/register">Register</Link>
        {isManager && <Link className="btn" to="/larry">Larry AI</Link>}
        <button onClick={() => saveSession(null)}>Sign out</button>
      </header>
      <nav className="m-tabs" aria-label="Phone tools">
        {([['ask', 'Ask AI'], ...(isManager ? [['lazy', 'Lazy AI']] as const : []), ['check', 'Check'], ['receive', 'Receive'], ['presell', 'Pre-sell'], ['lottery', 'Lottery']] as const).map(([k, label]) => (
          <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{label}</button>))}
      </nav>
      <main className="m-body">
        {tab === 'ask' && <div className="m-ask"><ShaileshChat page="/m" /></div>}
        {tab === 'lazy' && isManager && <LazyAI />}
        {tab === 'check' && <Check isManager={isManager} />}
        {tab === 'receive' && <Receive isManager={isManager} />}
        {tab === 'presell' && <PreSell />}
        {tab === 'lottery' && <LotteryAudit />}
      </main>
    </div>
  );
}

/** A scan box that also takes the camera or a Bluetooth scanner (which types digits and presses Enter). */
function ScanBox({ onScan, placeholder, busy }: { onScan: (v: string) => void; placeholder: string; busy?: boolean }) {
  const [v, setV] = useState('');
  const [cam, setCam] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="stack-s">
      <form className="scanbar" onSubmit={(e) => { e.preventDefault(); const t = v.trim(); setV(''); if (t) onScan(t); ref.current?.focus(); }}>
        <input ref={ref} value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} aria-label={placeholder} autoComplete="off" disabled={busy} />
        <button className="primary big">Go</button>
      </form>
      {cameraScanSupported() && !cam && <button onClick={() => setCam(true)}>Scan with camera</button>}
      {cam && <BarcodeScanner onClose={() => setCam(false)} onCode={onScan} />}
    </div>
  );
}

// ---------------- check a price, change it, record breakage ----------------
function Check({ isManager }: { isManager: boolean }) {
  const [item, setItem] = useState<any>(null);
  const [matches, setMatches] = useState<any[]>([]);
  const [notFound, setNotFound] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [msg, setMsg] = useState('');

  async function find(q: string) {
    setErr(null); setMsg(''); setMatches([]); setNotFound(null);
    try {
      if (/^\d{6,14}$/.test(q.replace(/[\s-]/g, ''))) {
        const r = await api<any>(`/api/scan/${encodeURIComponent(q)}`);
        if (r.source === 'store') setItem({ ...r.item, brand: r.product.brand, name: r.product.name, size_ml: r.product.size_ml, category: r.product.category });
        else { setItem(null); setNotFound(r.source === 'catalog' ? `${r.product.brand} ${r.product.name} is in the shared catalog but not in your store.` : 'That barcode is not in the catalog.'); }
      } else {
        const rows = await api<any[]>(`/api/items?q=${encodeURIComponent(q)}&limit=10`);
        if (rows.length === 1) setItem(rows[0]); else { setItem(null); setMatches(rows); if (!rows.length) setNotFound(`No item matches “${q}”.`); }
      }
    } catch (e) { setErr(e); }
  }

  return (
    <div className="stack">
      <ScanBox onScan={find} placeholder="Scan a barcode or type a name" />
      <ErrorNote error={err} />
      {msg && <div className="notice good">{msg}</div>}
      {matches.map((m) => (
        <button key={m.id} className="ghost m-row" onClick={() => { setItem(m); setMatches([]); }}>
          <span>{m.brand} {m.name} · {sizeLabel(m.size_ml)}</span><b>{usd(m.retail_cents)}</b></button>))}
      {notFound && (
        <div className="notice">{notFound} {isManager && <button onClick={() => setAdding(true)}>Add from master list</button>}</div>
      )}
      {item && <ItemCard item={item} isManager={isManager} onChanged={(m) => { setMsg(m); find(item.codes?.[0] ?? `${item.brand} ${item.name}`); }} />}
      {adding && <MasterSearch onClose={() => setAdding(false)} onAdded={(it, p) => { setAdding(false); setNotFound(null); setItem({ ...it, brand: p.brand, name: p.name, size_ml: p.size_ml, category: p.category, on_hand: 0 }); }} />}
    </div>
  );
}

function ItemCard({ item, isManager, onChanged }: { item: any; isManager: boolean; onChanged: (msg: string) => void }) {
  const [price, setPrice] = useState((item.retail_cents / 100).toFixed(2));
  const [broken, setBroken] = useState('');
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => { setPrice((item.retail_cents / 100).toFixed(2)); setBroken(''); }, [item.id, item.retail_cents]);
  const cents = Math.round(Number(price) * 100);
  const below = item.last_unit_cost_cents != null && cents < item.last_unit_cost_cents;
  return (
    <div className="panel"><div className="panel-body stack">
      <div><div style={{ fontWeight: 700, fontSize: 18 }}>{item.brand} {item.name}</div><div className="faint">{sizeLabel(item.size_ml)} · {item.category?.replace('_', ' ')}</div></div>
      <div className="stats">
        <div><div className="v">{usd(item.retail_cents)}</div><div className="l">Shelf price</div></div>
        <div><div className="v">{item.on_hand ?? 0}</div><div className="l">On hand</div></div>
        {isManager && <div><div className="v">{usd(item.last_unit_cost_cents)}</div><div className="l">Last cost</div></div>}
      </div>
      <ErrorNote error={err} />
      {isManager && (
        <>
          <div className="field-row">
            <label>New price<input className="num-input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))} /></label>
            <button className="primary" disabled={!(cents >= 0) || cents === item.retail_cents || below} onClick={async () => {
              setErr(null);
              try { await api(`/api/items/${item.id}`, { method: 'PATCH', body: { retailCents: cents } }); onChanged(`Price is now ${usd(cents)}. Print a new shelf label from Shelf labels.`); } catch (e) { setErr(e); }
            }}>Change price</button>
          </div>
          {below && <div className="notice bad">That is under your legal cost of {usd(item.last_unit_cost_cents)}.</div>}
          <div className="field-row">
            <label>Broken or missing bottles<input inputMode="numeric" value={broken} onChange={(e) => setBroken(e.target.value.replace(/\D/g, ''))} placeholder="0" /></label>
            <button disabled={!Number(broken)} onClick={async () => {
              setErr(null);
              try { await api(`/api/items/${item.id}/adjust`, { body: { reason: 'breakage', qty: Number(broken) } }); onChanged(`${broken} removed from stock as breakage.`); } catch (e) { setErr(e); }
            }}>Record breakage</button>
          </div>
        </>
      )}
    </div></div>
  );
}

// ---------------- receive a delivery ----------------
function Receive({ isManager }: { isManager: boolean }) {
  const list = useLoad(() => api<any[]>('/api/deliveries'), []);
  const pos = useLoad(() => (isManager ? api<any[]>('/api/purchase-orders').catch(() => []) : Promise.resolve([])), [isManager]);
  const [open, setOpen] = useState<string | null>(null);
  const [po, setPo] = useState('');
  const [err, setErr] = useState<unknown>(null);
  if (open) return <Delivery id={open} onBack={() => { setOpen(null); list.reload(); }} />;
  const openPos = (pos.data ?? []).filter((p) => p.status === 'sent' || p.status === 'partial');
  return (
    <div className="stack">
      <p className="muted">Count what comes off the truck. Nothing changes in your stock yet; the invoice does that. This catches shorts and extras.</p>
      <ErrorNote error={err ?? list.error} />
      <div className="panel"><div className="panel-body stack-s">
        {openPos.length > 0 && <label>Delivery for<select value={po} onChange={(e) => setPo(e.target.value)}>
          <option value="">No purchase order</option>{openPos.map((p) => <option key={p.id} value={p.id}>{p.po_number} · {p.wholesaler_name}</option>)}
        </select></label>}
        <button className="primary big" onClick={async () => {
          setErr(null);
          try { setOpen((await api<any>('/api/deliveries', { body: po ? { poId: po } : {} })).id); } catch (e) { setErr(e); }
        }}>Start receiving</button>
      </div></div>
      {list.data?.map((d) => (
        <button key={d.id} className="ghost m-row" onClick={() => setOpen(d.id)}>
          <span>{d.po_number ?? d.wholesaler ?? 'Delivery'} · {new Date(d.created_at).toLocaleDateString()}</span>
          <span><span className={`chip ${d.status === 'open' ? 'warn' : 'good'}`}>{d.status}</span> {d.units} units</span></button>))}
    </div>
  );
}

function Delivery({ id, onBack }: { id: string; onBack: () => void }) {
  const d = useLoad(() => api<any>(`/api/deliveries/${id}`), [id]);
  const [err, setErr] = useState<unknown>(null);
  const [last, setLast] = useState('');
  const open = d.data?.status === 'open';
  async function scan(code: string) {
    setErr(null);
    try { const r = await api<any>(`/api/deliveries/${id}/scan`, { body: { code, qty: 1, mode: 'add' } }); setLast(`${r.brand} ${r.name}: ${r.counted}`); d.reload(); } catch (e) { setErr(e); }
  }
  async function setQty(itemId: string, qty: number) {
    setErr(null);
    try { await api(`/api/deliveries/${id}/scan`, { body: { itemId, qty, mode: 'set' } }); d.reload(); } catch (e) { setErr(e); }
  }
  return (
    <div className="stack">
      <div className="row" style={{ display: 'flex', gap: 8 }}>
        <button onClick={onBack}>Back</button>
        <div className="grow"><b>{d.data?.po_number ?? d.data?.wholesaler ?? 'Delivery'}</b> <span className="faint">{d.data?.status}</span></div>
        {open && <button className="primary" onClick={async () => { try { await api(`/api/deliveries/${id}/close`, { body: {} }); d.reload(); } catch (e) { setErr(e); } }}>Finish</button>}
      </div>
      <ErrorNote error={err ?? d.error} />
      {open && <ScanBox onScan={scan} placeholder="Scan each bottle or case" />}
      {last && <div className="notice good">{last}</div>}
      <div className="panel">
        {d.data?.lines.map((l: any) => (
          <div key={l.item_id} className="m-line">
            <div className="grow"><div style={{ fontWeight: 600 }}>{l.brand} {l.name}</div><div className="faint">{sizeLabel(l.size_ml)}{l.expected != null ? ` · ordered ${l.expected}` : d.data.hasPo ? ' · not on the PO' : ''}</div></div>
            {open
              ? <input style={{ width: 64, textAlign: 'right' }} inputMode="numeric" defaultValue={l.counted} key={`${l.item_id}-${l.counted}`} onBlur={(e) => { const v = Number(e.target.value.replace(/\D/g, '')); if (v !== l.counted) setQty(l.item_id, v); }} />
              : <b>{l.counted}</b>}
            {l.diff != null && l.diff !== 0 && <span className={`chip ${l.diff < 0 ? 'bad' : 'warn'}`}>{l.diff < 0 ? `short ${-l.diff}` : `extra ${l.diff}`}</span>}
          </div>))}
        {d.data?.lines.length === 0 && <div className="empty">Nothing counted yet.</div>}
      </div>
    </div>
  );
}

// ---------------- pre-sell: build a cart on the phone, finish it at the register ----------------
function PreSell() {
  const held = useLoad(() => api<any[]>('/api/held-carts'), []);
  const [lines, setLines] = useState<{ item: any; qty: number }[]>([]);
  const [customer, setCustomer] = useState<any>(null);
  const [picking, setPicking] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const total = lines.reduce((s, l) => s + l.item.retail_cents * l.qty, 0);

  async function add(q: string) {
    setErr(null);
    try {
      let it: any = null;
      if (/^\d{6,14}$/.test(q.replace(/[\s-]/g, ''))) {
        const r = await api<any>(`/api/scan/${encodeURIComponent(q)}`);
        if (r.source !== 'store') throw new Error('That product is not in your store.');
        it = { ...r.item, brand: r.product.brand, name: r.product.name, size_ml: r.product.size_ml };
      } else {
        const rows = await api<any[]>(`/api/items?q=${encodeURIComponent(q)}&limit=1`);
        if (!rows.length) throw new Error(`No item matches “${q}”.`);
        it = rows[0];
      }
      setLines((ls) => { const i = ls.findIndex((l) => l.item.id === it.id); return i >= 0 ? ls.map((l, j) => (j === i ? { ...l, qty: l.qty + 1 } : l)) : [...ls, { item: it, qty: 1 }]; });
    } catch (e) { setErr(e); }
  }

  async function send() {
    setErr(null);
    try {
      const r = await api<any>('/api/held-carts', { body: { lines: lines.map((l) => ({ itemId: l.item.id, qty: l.qty })), customerId: customer?.id ?? null } });
      setSent(r.code); setLines([]); setCustomer(null); held.reload();
    } catch (e) { setErr(e); }
  }

  return (
    <div className="stack">
      <p className="muted">Ring up people waiting in line. Send the cart to the register and give them the number.</p>
      <ScanBox onScan={add} placeholder="Scan or type an item" />
      <ErrorNote error={err} />
      <div className="panel">
        {lines.map((l, i) => (
          <div key={l.item.id} className="m-line">
            <div className="grow"><div style={{ fontWeight: 600 }}>{l.item.brand} {l.item.name}</div><div className="faint">{sizeLabel(l.item.size_ml)} · {usd(l.item.retail_cents)}</div></div>
            <button onClick={() => setLines(lines.map((x, j) => (j === i ? { ...x, qty: Math.max(1, x.qty - 1) } : x)))}>−</button>
            <b>{l.qty}</b>
            <button onClick={() => setLines(lines.map((x, j) => (j === i ? { ...x, qty: x.qty + 1 } : x)))}>+</button>
            <button className="ghost danger" aria-label="Remove" onClick={() => setLines(lines.filter((_, j) => j !== i))}>✕</button>
          </div>))}
        {lines.length === 0 && <div className="empty">No items yet.</div>}
      </div>
      <div className="row" style={{ display: 'flex', gap: 8 }}>
        <button className="grow" onClick={() => setPicking(true)}>{customer ? `${customer.name} · ${customer.points} pts` : 'Add a customer'}</button>
        <button className="primary big" disabled={!lines.length} onClick={send}>Send to register · {usd(total)}</button>
      </div>
      {held.data && held.data.length > 0 && (
        <div className="panel"><div className="panel-head"><h2>Waiting at the register</h2></div>
          {held.data.map((h) => (
            <div key={h.id} className="m-line">
              <div className="grow"><div style={{ fontWeight: 700, fontSize: 18 }}>#{h.code}</div><div className="faint">{h.lines.length} items · {usd(h.total_cents)}{h.customer_name ? ` · ${h.customer_name}` : ''}</div></div>
              <button onClick={async () => { await api(`/api/held-carts/${h.id}`, { method: 'DELETE' }).catch(setErr); held.reload(); }}>Cancel</button>
            </div>))}
        </div>
      )}
      {picking && <CustomerPicker onClose={() => setPicking(false)} onPick={(c) => { setCustomer(c); setPicking(false); }} />}
      {sent && (
        <Modal title="Sent to the register" onClose={() => setSent(null)} footer={<button className="primary big" onClick={() => setSent(null)}>Next customer</button>}>
          <p>Tell the customer this number. The cashier types it under Recall.</p>
          <div style={{ fontSize: 56, fontWeight: 800, textAlign: 'center', letterSpacing: 4 }}>{sent}</div>
        </Modal>
      )}
    </div>
  );
}
